import { TransactionTypesEnum } from '@interfaces/models';
import { createSelector } from '@ngrx/store';
import { State } from '@store/index';
import { transactionsAdapter } from '@store/reducers/transaction-state/transaction-state.reducer';

const { selectIds, selectEntities, selectAll, selectTotal } = transactionsAdapter.getSelectors();

export const selectStateTransactionIds = createSelector(
  (state: State) => state.transactions,
  (adaptedState) => selectIds(adaptedState),
);

export const selectAllTransactions = createSelector(
  (state: State) => state.transactions,
  (adaptedState) => selectAll(adaptedState),
);

export const selectTransactionsTotal = createSelector(
  (state: State) => state.transactions,
  (adaptedState) => selectTotal(adaptedState),
);

export const selectStateTransactions = createSelector(
  (state: State) => state.transactions,
  (adaptedState) => selectEntities(adaptedState),
);

export const selectTransactionById = (transactionId: string) =>
  createSelector(selectStateTransactions, (transactions) => transactions[transactionId]);

export const selectCurrentTransactionId = createSelector(
  (state: State) => state,
  (state) => state.currentTransactionId,
);

export const selectCurrentTransaction = createSelector(
  selectStateTransactions,
  selectCurrentTransactionId,
  (transactions, currentTransactionId) =>
    !!currentTransactionId ? transactions[currentTransactionId] : null,
);

export const selectCurrentTransactionExist = createSelector(
  selectCurrentTransaction,
  (transaction) => !!transaction,
);

export const selectCurrentTransactionType = createSelector(
  selectCurrentTransaction,
  (transaction) =>
    !!transaction
      ? (transaction.selectedClientAccounts?.transactionType ?? TransactionTypesEnum.UNKNOWN)
      : TransactionTypesEnum.UNKNOWN,
);

export const selectIsCurrentTransactionNewAccountTransaction = createSelector(
  selectCurrentTransactionType,
  (transactionType) => transactionType === TransactionTypesEnum.NEW_ACCOUNT,
);

export const selectIsMultipleNewAccountTransactionExist = createSelector(
  selectAllTransactions,
  (transactions) =>
    transactions.length > 1 &&
    transactions.filter(
      (transaction) =>
        transaction.selectedClientAccounts?.transactionType === TransactionTypesEnum.NEW_ACCOUNT,
    ).length > 1,
);

export const selectIsNewAccountTransactionExist = createSelector(
  selectAllTransactions,
  (transactions) =>
    transactions.length > 0 &&
    Boolean(
      transactions.find(
        (transaction) =>
          transaction.selectedClientAccounts?.transactionType === TransactionTypesEnum.NEW_ACCOUNT,
      ),
    ),
);

export const selectCurrentTransactionInvestmentObjectives = createSelector(
  selectCurrentTransaction,
  (transaction) => transaction?.investmentObjectives ?? null,
);

export const selectCurrentTransactionSelectedAccounts = createSelector(
  selectCurrentTransaction,
  (transaction) => transaction?.selectedClientAccounts ?? null,
);

export const selectCurrentTransactionTransferTo = createSelector(
  selectCurrentTransactionSelectedAccounts,
  (selectedAccount) => selectedAccount?.transferTo ?? null,
);

export const selectMultiAccountType = createSelector(
  selectCurrentTransaction,
  (transaction) => transaction?.accountTriage?.accountTypeInheriting ?? null,
);

export const selectMultiIraType = createSelector(
  selectCurrentTransactionTransferTo,
  (transferTo) => transferTo?.iraType ?? null,
);

export const selectMultiIraOption = createSelector(
  selectCurrentTransactionTransferTo,
  (transferTo) => transferTo?.iraOption ?? null,
);
